import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { Globe, Plus, Trash2, RefreshCw, CheckCircle, XCircle, AlertCircle, Eye, EyeOff, Edit2, X, Save } from 'lucide-react'

interface ProxyServer {
  id: number
  name: string
  is_active: boolean
  is_healthy: boolean
  priority: number
  failure_count: number
  notes: string | null
  last_used_at: string | null
  last_health_check_at: string | null
  created_at: string
  updated_at: string
}

interface PoolStats {
  total: number
  active: number
  healthy: number
  unhealthy: number
  inactive: number
}

interface ProxyForm {
  name: string
  url: string
  priority: number
  notes: string
}

const emptyForm: ProxyForm = { name: '', url: '', priority: 0, notes: '' }

export default function AdminProxiesPage() {
  const [proxies, setProxies] = useState<ProxyServer[]>([])
  const [stats, setStats] = useState<PoolStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<ProxyForm>(emptyForm)
  const [showUrl, setShowUrl] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [resettingId, setResettingId] = useState<number | null>(null)

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const [proxiesRes, statsRes] = await Promise.all([
        api.get('/admin/proxies'),
        api.get('/admin/proxies/stats'),
      ])
      setProxies(proxiesRes.data.items || [])
      setStats(statsRes.data)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const openCreate = () => {
    setEditingId(null)
    setForm(emptyForm)
    setFormError(null)
    setShowUrl(false)
    setShowForm(true)
  }

  const openEdit = (proxy: ProxyServer) => {
    setEditingId(proxy.id)
    setForm({ name: proxy.name, url: '', priority: proxy.priority, notes: proxy.notes || '' })
    setFormError(null)
    setShowUrl(false)
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingId(null)
    setForm(emptyForm)
    setFormError(null)
  }

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Le nom est requis'); return }
    if (!editingId && !form.url.trim()) { setFormError("L'URL est requise"); return }
    if (form.url && !form.url.match(/^(https?|socks5):\/\//)) {
      setFormError("L'URL doit commencer par http://, https:// ou socks5://")
      return
    }

    setSaving(true)
    setFormError(null)
    try {
      if (editingId) {
        const payload: any = { name: form.name, priority: form.priority, notes: form.notes || null }
        if (form.url.trim()) payload.url = form.url
        await api.put(`/admin/proxies/${editingId}`, payload)
      } else {
        await api.post('/admin/proxies', {
          name: form.name,
          url: form.url,
          priority: form.priority,
          notes: form.notes || null,
        })
      }
      closeForm()
      await load()
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Supprimer ce proxy ?')) return
    setDeletingId(id)
    try {
      await api.delete(`/admin/proxies/${id}`)
      await load()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression')
    } finally {
      setDeletingId(null)
    }
  }

  const handleResetHealth = async (id: number) => {
    setResettingId(id)
    try {
      await api.post(`/admin/proxies/${id}/reset-health`)
      await load()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la réinitialisation')
    } finally {
      setResettingId(null)
    }
  }

  const handleToggleActive = async (proxy: ProxyServer) => {
    try {
      await api.put(`/admin/proxies/${proxy.id}`, { is_active: !proxy.is_active })
      await load()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur')
    }
  }

  const formatDate = (d: string | null) => {
    if (!d) return '—'
    return new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
            <Globe className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Pool de Proxies</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              Gestion centralisée des proxies HTTP pour les serveurs MCP (Click&amp;Boat)
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="p-2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            title="Rafraîchir"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm"
          >
            <Plus className="w-4 h-4" />
            Ajouter un proxy
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total', value: stats.total, color: 'text-zinc-700 dark:text-zinc-300' },
            { label: 'Actifs', value: stats.active, color: 'text-blue-600 dark:text-blue-400' },
            { label: 'Sains', value: stats.healthy, color: 'text-emerald-600 dark:text-emerald-400' },
            { label: 'Défaillants', value: stats.unhealthy, color: 'text-red-600 dark:text-red-400' },
            { label: 'Inactifs', value: stats.inactive, color: 'text-zinc-400 dark:text-zinc-500' },
          ].map(s => (
            <div key={s.label} className="bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 p-3 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Security notice */}
      <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800 dark:text-amber-200">
          <strong>Sécurité :</strong> Les URLs des proxies (incluant les identifiants) sont chiffrées en base de données avec Fernet AES-128.
          Elles ne sont jamais affichées ni transmises aux utilisateurs. Le serveur MCP Click&amp;Boat reçoit le proxy en round-robin
          automatiquement à chaque démarrage de session.
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : proxies.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700">
          <Globe className="w-12 h-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500 dark:text-zinc-400 font-medium">Aucun proxy configuré</p>
          <p className="text-sm text-zinc-400 dark:text-zinc-500 mt-1">
            Ajoutez des proxies pour que Click&amp;Boat les utilise en rotation automatique
          </p>
          <button
            onClick={openCreate}
            className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
          >
            Ajouter le premier proxy
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {proxies.map(proxy => (
            <div
              key={proxy.id}
              className={`bg-white dark:bg-zinc-800 rounded-xl border transition-colors ${
                proxy.is_active && proxy.is_healthy
                  ? 'border-zinc-200 dark:border-zinc-700'
                  : proxy.is_active && !proxy.is_healthy
                  ? 'border-red-200 dark:border-red-800'
                  : 'border-zinc-100 dark:border-zinc-800 opacity-60'
              }`}
            >
              <div className="p-4 flex items-center gap-4">
                {/* Status icon */}
                <div className="flex-shrink-0">
                  {!proxy.is_active ? (
                    <div className="w-10 h-10 rounded-full bg-zinc-100 dark:bg-zinc-700 flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-zinc-400" />
                    </div>
                  ) : proxy.is_healthy ? (
                    <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-zinc-900 dark:text-zinc-100">{proxy.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400">
                      Priorité {proxy.priority}
                    </span>
                    {!proxy.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-700 text-zinc-400">Inactif</span>
                    )}
                    {proxy.is_active && !proxy.is_healthy && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                        Défaillant ({proxy.failure_count} erreur{proxy.failure_count > 1 ? 's' : ''})
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-400 dark:text-zinc-500 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                    <span>URL : <span className="font-mono">••••••••••••••••</span></span>
                    <span>Dernière utilisation : {formatDate(proxy.last_used_at)}</span>
                    {proxy.notes && <span>Note : {proxy.notes}</span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {proxy.is_active && !proxy.is_healthy && (
                    <button
                      onClick={() => handleResetHealth(proxy.id)}
                      disabled={resettingId === proxy.id}
                      className="p-2 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                      title="Remettre en état sain"
                    >
                      <RefreshCw className={`w-4 h-4 ${resettingId === proxy.id ? 'animate-spin' : ''}`} />
                    </button>
                  )}
                  <button
                    onClick={() => handleToggleActive(proxy)}
                    className={`p-2 rounded-lg transition-colors ${
                      proxy.is_active
                        ? 'text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700'
                        : 'text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                    }`}
                    title={proxy.is_active ? 'Désactiver' : 'Activer'}
                  >
                    {proxy.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => openEdit(proxy)}
                    className="p-2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(proxy.id)}
                    disabled={deletingId === proxy.id}
                    className="p-2 text-zinc-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={closeForm} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between p-6 border-b border-zinc-200 dark:border-zinc-700">
                <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
                  {editingId ? 'Modifier le proxy' : 'Ajouter un proxy'}
                </h2>
                <button onClick={closeForm} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                    {formError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Ex: Proxy Paris #1"
                    className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    URL du proxy {!editingId && <span className="text-red-500">*</span>}
                    {editingId && <span className="text-zinc-400 font-normal ml-1">(laisser vide pour ne pas changer)</span>}
                  </label>
                  <div className="relative">
                    <input
                      type={showUrl ? 'text' : 'password'}
                      value={form.url}
                      onChange={e => setForm({ ...form, url: e.target.value })}
                      placeholder="http://user:password@host:port"
                      className="w-full px-3 py-2 pr-10 border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowUrl(!showUrl)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                    >
                      {showUrl ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                    Chiffrée avec AES-128 (Fernet) — jamais visible après enregistrement
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    Priorité
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={form.priority}
                    onChange={e => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                    Plus élevé = utilisé en priorité dans la rotation
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    Notes internes
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={e => setForm({ ...form, notes: e.target.value })}
                    placeholder="Ex: Datacenter OVH Paris, renouvellement le 01/06"
                    rows={2}
                    className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 p-6 pt-0">
                <button
                  onClick={closeForm}
                  className="flex-1 px-4 py-2 border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors text-sm font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors text-sm font-medium"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
